/**
 * Copyright © 2025 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React, { useState, useEffect, useContext, useRef } from "react";
import { useLocation } from "react-router-dom";
import PrimarySidebar from "./components/primary_sidebar";
import Header from "./components/header";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import "./styles.css";
import { AbilityContext } from "./contexts/abilityContext";
import { useDispatch, useSelector } from "react-redux";
import { fetchAdmin } from "./store/adminSlice";
import { useNotificationContext } from "./contexts/notificationContext";
import { useNavigate } from "react-router-dom";
import AddBusinessIcon from "@mui/icons-material/AddBusiness";
import { togglePrimarySidebar } from "./store/sidebarSlice";
import { strictCanScope } from "./utils/caslStrict";
import DescriptionIcon from '@mui/icons-material/Description';
import StoreMallDirectoryIcon from '@mui/icons-material/StoreMallDirectory';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import { CreditCardFilled } from '@ant-design/icons';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import PersonAddAltRoundedIcon from '@mui/icons-material/PersonAddAltRounded';
import WorkIcon from '@mui/icons-material/Work';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import FindInPageIcon from '@mui/icons-material/FindInPage';
import { checkAdmin } from "./utils/checkAdmin";
import DashboardIcon from '@mui/icons-material/Dashboard';
import PolicyIcon from '@mui/icons-material/Policy';
import { ReactComponent as CirclePercentIcon } from "./assets/images/CirclePercent.svg";
import { useTheme, useMediaQuery, Drawer } from "@mui/material";
import mobileLogo from "./assets/images/OT_only_name.png"
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import FastfoodIcon from '@mui/icons-material/Fastfood';

const iconStyles = {
  creditCard: { fontSize: '18px', padding: '5px 5px 5px 3px' },
  work: { fontSize: '22px', padding: '1px 2px 1px 1px' },
};

const SELF_EXPANDING_TAB_IDS = ["logs", "restaurant_management"];

export default function Layout({ children }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { updateAbility } = useContext(AbilityContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { isPrimaryCollapsed } = useSelector((state) => state.sidebar);
  const showNotication = useNotificationContext();
  const dispatch = useDispatch();
  const { admin } = useSelector((state) => state.admin);
  const superAdmin = checkAdmin(admin?.formatted_roles)
  const { ability } = useContext(AbilityContext)
  const abilityPresent = ability?.rules
  const isAbilityLoaded = abilityPresent?.length > 0
  const restaurantGroup = (ability.can('create', 'Restaurant_Group') || ability.can('read', 'Restaurant_Group'))
  const restaurant = (ability.can('read', 'Restaurant_Group') || ability.can('read', 'Restaurant'))
  const discount = ability.can('read', 'Restaurant_Group')
  const networkGuest = ability.can('read', 'Restaurant')
  const isAdmin = (strictCanScope(ability, 'read', 'Restaurant_Group'))
  const hasLoadedAdmin = useRef(false)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("md"))

  const viewPIIReports = strictCanScope(
    ability,
    "read",
    "Reports",
    "ReportsAPIMockObject"
  );
  const ifSuperAdmin = !(superAdmin?.role === 'admin' || superAdmin?.role === 'viewer')

  const pathToTabIdMap = {
    "/admin/home": "reservations",
    "/admin/network_guests": "network_guests",
    "/admin/employees": "network_guests",
    "/admin/jobs": "jobs",
    "/admin/restaurant": "restaurants",
    "/admin/reports/homepage": "reports",
    "/admin/reports/perq_report": "reports",
    "/admin/reports/labor_report": "reports",
    "/admin/reports/daily_logbook": "reports",
    "/admin/reports/daily_sales": "reports",
    "/admin/reports/employee-perqs": "reports",
    "/admin/user_management": "user_management",
    "/admin/perq_cards": "perq_cards",
    "/admin/groups": "restaurants_group",
    "/admin/reports/tip-compliance-report": "reports",
    "/admin/reports/check-in-compliance-report": "reports",
    "/admin/reports/employee-perq-report": "reports",
    "/admin/reports/restaurant-perq-report": "reports",
    "/admin/configurations/integrations": "configuration",
    "/admin/audit_logs": "logs",
    "/admin/reports/employee_visibility": 'reports',
    "/admin/reports/uninvited_guests": 'reports',
    "/admin/reports/employee_insights": 'reports',
    "/admin/reports/employee_time_entries": 'reports',
    "/admin/reports/global_search": "reports",
    "/admin/reports/named_user_perqs": "reports",
    "/admin/reports/classified_perq_records": "reports",
    "/admin/perq_card_logs": 'logs',
    "/admin/reports/mobile_user_activity": "reports",
    "/admin/reports/archived_employees": "reports",
    "/admin/reports/billing_report": "reports",
    "/admin/dashboard": "dashboard",
    "/admin/employee_groups": "employee_groups",
    "/admin/dynamic_perqs": "dynamic_perqs",
    "/admin/configured_perqs": "configured_perqs",
    "/admin/import_logs": "logs",
    "/admin/policy_engine": "policy_engine",
    "/admin/revenue_centers": "restaurant_management",
    "/admin/sales_categories": "restaurant_management",
    "/admin/meal_periods": "restaurant_management",
  };

  const pathToSectionMap = {
    "/admin/reports/homepage": "sales",
    "/admin/reports/perq_report": "sales",
    "/admin/reports/labor_report": "sales",
    "/admin/reports/daily_logbook": "sales",
    "/admin/reports/daily_sales": "sales",
    "/admin/reports/employee-perqs": "perq-reports",
    "/admin/reports/employee-perq-report": "perq-reports",
    "/admin/reports/restaurant-perq-report": "perq-reports",
    "/admin/reports/tip-compliance-report": "perq-reports",
    "/admin/reports/check-in-compliance-report": "perq-reports",
    "/admin/reports/named_user_perqs": "perq-reports",
    "/admin/reports/classified_perq_records": "perq-reports",
    "/admin/reports/employee_visibility": "employee-reports",
    "/admin/reports/uninvited_guests": "employee-reports",
    "/admin/reports/global_search": "employee-reports",
    "/admin/reports/employee_insights": "employee-reports",
    "/admin/reports/employee_time_entries": "employee-reports",
    "/admin/reports/mobile_user_activity": "employee-reports",
    "/admin/reports/archived_employees": "employee-reports",
    "/admin/reports/billing_report": "ownerstable-reports"
  };

  useEffect(() => {
    let isMounted = true;

    const loadAdmin = async () => {
      const action = await dispatch(fetchAdmin());
      hasLoadedAdmin.current = true;
      if (isMounted && action.payload) {
        if (action.payload.formatted_roles?.length === 0) {
          navigate("/admin/no_permissions");
          showNotication("Your permissions has been updated!", "success");
        } else {
          updateAbility(action.payload.formatted_permissions);
        }
      }
    };

    if (!admin && !hasLoadedAdmin.current) {
      loadAdmin();
    } else if (admin?.formatted_permissions && ability.rules.length === 0 && !hasLoadedAdmin.current) {
      updateAbility(admin.formatted_permissions);
      hasLoadedAdmin.current = true;
    }

    const handleSessionRestored = async () => {
      const action = await dispatch(fetchAdmin());
      if (action.payload) {
        if (action.payload.formatted_roles?.length === 0) {
          navigate("/admin/no_permissions");
          showNotication("Your permissions has been updated!", "success");
        } else {
          updateAbility(action.payload.formatted_permissions);
        }
      }
    };

    window.addEventListener('session-restored', handleSessionRestored);

    return () => {
      isMounted = false;
      window.removeEventListener('session-restored', handleSessionRestored);
    };
  }, [dispatch]);

  const [activeTab, setActiveTab] = useState("");
  const [expandedItems, setExpandedItems] = useState(new Set(["reports", "sales"]));
  const [userManuallyToggled, setUserManuallyToggled] = useState(new Set());

  useEffect(() => {
    const currentTabId = Object.entries(pathToTabIdMap).find(([path]) =>
      location.pathname.startsWith(path)
    )?.[1];

    if (currentTabId) {
      setActiveTab(currentTabId);

      if (currentTabId === "reports") {
        if (userManuallyToggled.size === 0) {
          const currentSection = Object.entries(pathToSectionMap).find(([path]) =>
            location.pathname.startsWith(path)
          )?.[1];

          if (currentSection) {
            setExpandedItems(new Set(["reports", currentSection]));
          } else if (location.pathname === "/admin/reports" || location.pathname === "/admin/reports/") {
            navigate("/admin/reports/homepage");
            setExpandedItems(new Set(["reports", "sales"]));
          }
        }
      }
      else if(SELF_EXPANDING_TAB_IDS.includes(currentTabId)){
        setExpandedItems(new Set([currentTabId]));
      }
      else {
        setExpandedItems(new Set());
        setUserManuallyToggled(new Set());
      }
    }
  }, [location.pathname]);

  const toggleExpandedItem = (itemId) => {
    setUserManuallyToggled(prev => new Set([...prev, itemId]));

    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const handleReportsClickInCollapsedState = () => {
    if (isPrimaryCollapsed) {
      dispatch(togglePrimarySidebar());

      navigate("/admin/reports/homepage");
      setActiveTab("reports");
    }
  };

  const handleToggleCollapse = () => {
    if (isMobile) {
      setMobileOpen(true);
    } else {
      dispatch(togglePrimarySidebar());
    }
  }

  const primaryCategories = [
    {
      id: "visualization",
      label: "visualization",
      items: [
        {
          id: "dashboard",
          icon: <DashboardIcon />,
          label: "Dashboard",
          path: "/admin/dashboard",
          disabled: false,
          type: "simple"
        },
      ]
    },
    {
      id: "analytics",
      label: "ANALYTICS",
      items: [
        {
          id: "reports",
          icon: <DescriptionIcon />,
          label: "Reports",
          path: "",
          disabled: false,
          type: "expandable",
          children: [
            {
              id: "sales",
              label: "Sales",
              type: "expandable-section",
              children: [
                { id: "orders", label: "Orders", path: "/admin/reports/homepage", type: "item", permission: false },
                { id: "perq-report", label: "Perq Report", path: "/admin/reports/perq_report", type: "item", permission: !viewPIIReports },
                { id: "labor-report", label: "Labor Report", path: "/admin/reports/labor_report", type: "item", permission: false },
                { id: "daily-logbook", label: "Daily Logbook", path: "/admin/reports/daily_logbook", type: "item", permission: false },
                { id: "daily-sales", label: "Daily Sales", path: "/admin/reports/daily_sales", type: "item", permission: false },
              ]
            },
            {
              id: "perq-reports",
              label: "Perq Reports",
              type: "expandable-section",
              children: [
                { id: "employee-discount", label: "Employee Perqs", path: "/admin/reports/employee-perqs", type: "item", permission: false },
                { id: "employee", label: "Employee Perq Card Allocation", path: "/admin/reports/employee-perq-report", type: "item", permission: !viewPIIReports },
                { id: "restaurant", label: "Restaurant Perqs", path: "/admin/reports/restaurant-perq-report", type: "item", permission: !viewPIIReports },
                { id: "tip-compliance", label: "Tip Compliance", path: "/admin/reports/tip-compliance-report", type: "item", permission: !viewPIIReports },
                { id: "named-user-discount", label: "Known Guests Report", path:"/admin/reports/named_user_perqs", type: "item", permission: !viewPIIReports },
                { id: "check-in-compliance", label: "Check-In Compliance", path: "/admin/reports/check-in-compliance-report", type: "item", permission: !restaurant },
              ]
            },
            {
              id: "employee-reports",
              label: "Employee Reports",
              type: "expandable-section",
              children: [
                { id: "employees", label: "Employees Without Number", path: "/admin/reports/employee_visibility", type: "item", permission: false },
                { id: "unknown-network", label: "Unknown Network Guests", path: "/admin/reports/uninvited_guests", type: "item", permission: ifSuperAdmin },
                { id: "employee-search", label: "Employee Search", path: "/admin/reports/global_search", type: "item", permission: ifSuperAdmin },
                { id: "employee-insights", label: "Employee Insights", path: "/admin/reports/employee_insights", type: "item", permission: ifSuperAdmin },
                { id: "employee-shift-report", label: "Employee Shift Report", path: "/admin/reports/employee_time_entries", type: "item", permission: false },
                { id: "mobile_user_acitivity", label: "Mobile User Activity", path: "/admin/reports/mobile_user_activity", type: "item", permission: false },
                { id: "archived-employees-report", label: "Archived Employees", path: "/admin/reports/archived_employees", type: "item", permission: ifSuperAdmin }
              ]
            },
            // Left out, not greyed: restaurant-group-scoped roles must not see that this report exists.
            ...(ifSuperAdmin ? [] : [{
              id: "ownerstable-reports",
              label: "OwnersTable Reports",
              type: "expandable-section",
              children: [
                { id: "billing-report", label: "Billing Report", path: "/admin/reports/billing_report", type: "item", permission: false },
              ]
            }])
          ]
        },
        {
          id: "logs",
          icon: <FindInPageIcon />,
          label: "Logs",
          path: "",
          disabled: false,
          type: "expandable",
          children: [
            {
              id: "audit_logs",
              label: "Audit Logs",
              type: "non-expandable-section",
              path: '/admin/audit_logs',
              permission: ifSuperAdmin
            },
            {
              id: "perq_card_logs",
              label: 'Perq Card Logs',
              type: "non-expandable-section",
              path: '/admin/perq_card_logs',
              permission: ifSuperAdmin
            },
            {
              id: "import_logs",
              label: 'Import Logs',
              type: "non-expandable-section",
              path: '/admin/import_logs',
              permission: ifSuperAdmin
            },
          ]
        },
      ]
    },
    {
      id: "management",
      label: "MANAGEMENT",
      items: [
        {
          id: "restaurants_group",
          icon: <AddBusinessIcon />,
          label: "Restaurant Groups",
          path: !isAbilityLoaded || restaurantGroup ? "/admin/groups" : '',
          disabled: isAbilityLoaded ? !restaurantGroup : false,
          type: "simple"
        },
        {
          id: "restaurants",
          icon: <StoreMallDirectoryIcon />,
          label: "Restaurants",
          path: !isAbilityLoaded || restaurant ? "/admin/restaurant" : '',
          disabled: isAbilityLoaded ? !restaurant : false,
          type: "simple"
        },
        {
          id: "restaurant_management",
          icon: <FastfoodIcon />,
          label: "Restaurant Management",
          path: "",
          disabled: false,
          type: "expandable",
          children: [
            {
              id: "revenue_centers",
              label: "Revenue Center",
              type: "non-expandable-section",
              path: '/admin/revenue_centers',
              permission: isAbilityLoaded ? !restaurant : false
            },
            {
              id: "sales_categories",
              label: "Sales Category",
              type: "non-expandable-section",
              path: '/admin/sales_categories',
              permission: isAbilityLoaded ? !restaurant : false
            },
            {
              id: "meal_periods",
              label: "Meal Period",
              type: "non-expandable-section",
              path: '/admin/meal_periods',
              permission: isAbilityLoaded ? !restaurant : false
            },
          ]
        },
        {
          id: "configured_perqs",
          icon: <LocalOfferIcon />,
          label: "Configured Perqs",
          path: !isAbilityLoaded || restaurant ? "/admin/configured_perqs" : '',
          disabled: isAbilityLoaded ? !restaurant : false,
          type: "simple"
        },
        {
          id: "employee_groups",
          icon: <PeopleAltIcon />,
          label: "Employee Groups",
          path: !isAbilityLoaded || restaurantGroup ? "/admin/employee_groups" : '',
          disabled: isAbilityLoaded ? !restaurantGroup : false,
          type: "simple"
        },
        {
          id: "perq_cards",
          icon: <CreditCardFilled style={iconStyles.creditCard} />,
          label: "Perq Cards",
          path: !isAbilityLoaded || discount ? "/admin/perq_cards" : '',
          disabled: isAbilityLoaded ? !discount : false,
          type: "simple"
        },
        {
          id: "dynamic_perqs",
          icon: <CirclePercentIcon className="sidebar-circle-percent-icon" />,
          label: "Dynamic Perqs",
          path: !isAbilityLoaded || restaurantGroup ? "/admin/dynamic_perqs" : '',
          disabled: isAbilityLoaded ? !restaurantGroup : false,
          type: "simple"
        },
        {
          id: "jobs",
          icon: <WorkIcon style={iconStyles.work} />,
          label: "Jobs",
          path: !isAbilityLoaded || networkGuest ? "/admin/jobs" : '',
          disabled: isAbilityLoaded ? !networkGuest : false,
          type: "simple"
        },
        {
          id: "policy_engine",
          icon: <PolicyIcon />,
          label: "Policy Engine",
          path: !isAbilityLoaded || restaurant ? "/admin/policy_engine" : '',
          disabled: isAbilityLoaded ? !restaurant : false,
          type: "simple"
        },
        {
          id: "reservations",
          icon: <CalendarMonthIcon />,
          label: "Reservations",
          path: !isAbilityLoaded || isAdmin ? "/admin/home" : '',
          disabled: isAbilityLoaded ? !isAdmin : false,
          type: "simple"
        },
      ]
    },
    {
      id: "users",
      label: "USERS",
      items: [
        {
          id: "network_guests",
          icon: <GroupsRoundedIcon />,
          label: "Mobile App Users",
          path: !isAbilityLoaded || networkGuest ? "/admin/employees" : '',
          disabled: isAbilityLoaded ? !networkGuest : false,
          type: "simple"
        },
        {
          id: "user_management",
          icon: <PersonAddAltRoundedIcon />,
          label: "Admin App Users",
          path: !isAbilityLoaded || isAdmin ? "/admin/user_management" : '',
          disabled: isAbilityLoaded ? !isAdmin : false,
          type: "simple"
        },
      ]
    },
    {
      id: "system",
      label: "SYSTEM",
      items: [
        {
          id: "configuration",
          icon: <SettingsRoundedIcon />,
          label: "Configuration",
          path: !isAbilityLoaded || isAdmin ? "/admin/configurations/integrations" : '',
          disabled: isAbilityLoaded ? !isAdmin : false,
          type: "simple"
        },
      ]
    },
  ];

  return (
    <div className="app-container">
      <Header
        isCollapsed={isPrimaryCollapsed}
        toggleCollapse={handleToggleCollapse}
        admin={admin}
        isMobile={isMobile}
      />
      {
        isMobile ? (
          <Drawer
            className="mobile-sidebar"
            variant="temporary"
            open={mobileOpen}
            onClose={() => setMobileOpen(false)}
            ModalProps={{ keepMounted: true }}
             PaperProps={{
              sx: {
                width: 234,
                backgroundColor: 'white',
              }
            }}
          >
            <div className="mobile-logo-container">
              <img src={mobileLogo} alt="brand-logo" className="mobile-logo"/>
            </div>
            <PrimarySidebar
              categories={primaryCategories}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              isCollapsed={false}
              expandedItems={expandedItems}
              toggleExpandedItem={toggleExpandedItem}
              onReportsClickInCollapsedState={handleReportsClickInCollapsedState}
              isMobile={isMobile}
              setMobileOpen={setMobileOpen}
            />
          </Drawer>
        ) : (
          <PrimarySidebar
            categories={primaryCategories}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            isCollapsed={isPrimaryCollapsed}
            toggleCollapse={() => dispatch(togglePrimarySidebar())}
            abilityPresent={abilityPresent}
            expandedItems={expandedItems}
            toggleExpandedItem={toggleExpandedItem}
            onReportsClickInCollapsedState={handleReportsClickInCollapsedState}
          />
        )
      }
      <main
        className={`main-content ${
          isPrimaryCollapsed ? "primary-collapsed" : ""
        }`}
      >
        {children}
      </main>
    </div>
  );
}
